import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { UserPlus, Pencil, Trash2, Shield, X, Watch } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEAM_ROLES = [
  { value: 'front_office', label: 'Front Office' },
  { value: 'assistant', label: 'Assistant' },
  { value: 'hygienist', label: 'Hygienist' },
  { value: 'doctor', label: 'Doctor' },
  { value: 'office_manager', label: 'Office Manager' },
];

export function TeamSection({ darkMode, users, operatories, currentUser, fetchData, smartwatchSeats }) {
  // Add user form state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('front_office');
  const [isAdmin, setIsAdmin] = useState(false);
  const [assignedOperatories, setAssignedOperatories] = useState([]);
  const [drDisplayPreference, setDrDisplayPreference] = useState('last');
  const [loading, setLoading] = useState(false);

  // Edit user form state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editAssignedOperatories, setEditAssignedOperatories] = useState([]);
  const [editDrDisplayPreference, setEditDrDisplayPreference] = useState('last');
  const [editHasSmartwatchAccess, setEditHasSmartwatchAccess] = useState(false);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const getRoleLabel = (roleValue) => {
    const found = TEAM_ROLES.find(r => r.value === roleValue);
    return found ? found.label : roleValue;
  };

  const handleCreateUser = async () => {
    if (!username || !password || !firstName || !lastName) { toast.error('Please fill all required fields'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, {
        username, password, first_name: firstName, last_name: lastName,
        role: isAdmin ? 'admin' : role, base_role: role, is_admin: isAdmin,
        sound_enabled: true, assigned_operatories: assignedOperatories, dr_display_preference: drDisplayPreference
      });
      toast.success('Team member added');
      setUserDialogOpen(false);
      setUsername(''); setPassword(''); setFirstName(''); setLastName('');
      setRole('front_office'); setIsAdmin(false); setAssignedOperatories([]); setDrDisplayPreference('last');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : (typeof detail === 'string' ? detail : 'Failed to create user'));
    } finally { setLoading(false); }
  };

  const handleEditUser = (u) => {
    if (u.is_owner && currentUser?.id !== u.id) { toast.error('Only the owner can edit their own account.'); return; }
    setEditingUser(u);
    let fn = u.first_name || '';
    let ln = u.last_name || '';
    if (!fn && u.full_name) { const parts = u.full_name.split(' '); fn = parts[0] || ''; ln = parts.length > 1 ? parts.slice(1).join(' ') : ''; }
    setEditFirstName(fn); setEditLastName(ln); setEditUsername(u.username);
    setEditRole(u.base_role || (u.role === 'admin' ? 'office_manager' : u.role));
    setEditIsAdmin(u.role === 'admin' || u.is_admin);
    setEditAssignedOperatories(u.assigned_operatories || []);
    setEditDrDisplayPreference(u.dr_display_preference || 'last');
    setEditHasSmartwatchAccess(u.has_smartwatch_access || false);
    setEditDialogOpen(true);
  };

  const handleSaveUserEdit = async () => {
    if (!editFirstName || !editLastName || !editUsername) { toast.error('First name, last name, and username are required'); return; }
    setLoading(true);
    try {
      await axios.put(`${API}/users/${editingUser.id}`, {
        first_name: editFirstName, last_name: editLastName, username: editUsername,
        role: editIsAdmin ? 'admin' : editRole, base_role: editRole, is_admin: editIsAdmin,
        assigned_operatories: editAssignedOperatories, dr_display_preference: editDrDisplayPreference
      });
      toast.success('Team member updated');
      setEditDialogOpen(false); setEditingUser(null); fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update user'));
    } finally { setLoading(false); }
  };

  const handleToggleSmartwatchAccess = async (grant) => {
    if (!editingUser) return;
    try {
      if (grant) {
        if (!smartwatchSeats || smartwatchSeats.available <= 0) { toast.error('No smartwatch seats available. Please purchase more seats from the License page.'); return; }
        await axios.post(`${API}/license/smartwatch-seats/assign/${editingUser.id}`);
        toast.success('Smartwatch access granted');
      } else {
        await axios.post(`${API}/license/smartwatch-seats/unassign/${editingUser.id}`);
        toast.success('Smartwatch access removed');
      }
      setEditHasSmartwatchAccess(grant);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update smartwatch access'); }
  };

  const handleDeleteClick = (u) => {
    if (u.is_owner) { toast.error('Cannot delete the owner account.'); return; }
    if (u.role === 'admin' || u.is_admin) { toast.error('Cannot delete an admin. Remove admin status first.'); return; }
    setUserToDelete(u); setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success('Team member removed');
      setDeleteDialogOpen(false); setUserToDelete(null); fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : (typeof detail === 'string' ? detail : 'Failed to delete user'));
    } finally { setLoading(false); }
  };

  const handleProfilePictureUpload = async (userId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/users/${userId}/upload-picture`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Profile picture uploaded');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to upload picture'); }
  };

  const handleRemoveProfilePicture = async (userId) => {
    try { await axios.delete(`${API}/users/${userId}/picture`); toast.success('Profile picture removed'); fetchData(); }
    catch { toast.error('Failed to remove picture'); }
  };

  return (
    <>
      <div className={`glass-panel rounded-2xl p-6 ${darkMode ? 'bg-slate-800/50' : ''}`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-xl font-heading font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Team Members</h2>
          <div className="flex gap-2">
            <Button onClick={() => setEditMode(!editMode)} variant={editMode ? "default" : "outline"} className={editMode ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}>
              <Pencil className="w-4 h-4 mr-2" />{editMode ? 'Done Editing' : 'Edit Team'}
            </Button>
            
            <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-user-button" className="bg-dp-600 hover:bg-dp-700 text-white">
                  <UserPlus className="w-4 h-4 mr-2" />Add Team Member
                </Button>
              </DialogTrigger>
              <DialogContent className="glass-panel max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">Add Team Member</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name</Label>
                      <div className="flex items-center gap-2 mt-2">
                        {role === 'doctor' && <span className="text-slate-500 font-medium">Dr.</span>}
                        <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="David" className="flex-1" />
                      </div>
                    </div>
                    <div><Label>Last Name</Label><Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Bark" className="mt-2" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Username (Tag)</Label><Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="@drbark" className="mt-2" /></div>
                    <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2" /></div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Label>Role</Label>
                      <Select value={role} onValueChange={setRole}>
                        <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>{TEAM_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="w-40">
                      <Label className="text-center block">Admin Designation</Label>
                      <div className="flex items-center justify-center mt-4">
                        <Checkbox id="admin-checkbox" checked={isAdmin} onCheckedChange={setIsAdmin} className="h-5 w-5" />
                        <label htmlFor="admin-checkbox" className="ml-2 text-sm text-slate-600">Grant Admin</label>
                      </div>
                    </div>
                  </div>
                  {role === 'doctor' && (
                    <div className="p-3 rounded-lg bg-dp-50 border border-dp-200">
                      <Label className="mb-2 block text-dp-800">Display Name Preference</Label>
                      <p className="text-xs text-dp-600 mb-3">How should this doctor's name appear? (e.g., "Dr. {drDisplayPreference === 'first' ? firstName || 'David' : lastName || 'Bark'}")</p>
                      <div className="flex gap-4">
                        <div className="flex items-center"><Checkbox id="dr-pref-first" checked={drDisplayPreference === 'first'} onCheckedChange={(c) => c && setDrDisplayPreference('first')} /><label htmlFor="dr-pref-first" className="ml-2 text-sm text-slate-700 cursor-pointer">Dr. {firstName || 'FirstName'}</label></div>
                        <div className="flex items-center"><Checkbox id="dr-pref-last" checked={drDisplayPreference === 'last'} onCheckedChange={(c) => c && setDrDisplayPreference('last')} /><label htmlFor="dr-pref-last" className="ml-2 text-sm text-slate-700 cursor-pointer">Dr. {lastName || 'LastName'}</label></div>
                      </div>
                    </div>
                  )}
                  {!['front_desk', 'front_office', 'office_manager', 'assistant'].includes(role) && (
                    <div>
                      <Label className="mb-2 block">Assigned Operatories</Label>
                      <p className="text-xs text-slate-500 mb-3">Select operatories where this provider will work</p>
                      <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg bg-slate-50">
                        {operatories.map(op => (
                          <div key={op.id} className="flex items-center space-x-2">
                            <Checkbox id={`new-op-${op.id}`} checked={assignedOperatories.includes(op.id)} onCheckedChange={(c) => { if (c) setAssignedOperatories([...assignedOperatories, op.id]); else setAssignedOperatories(assignedOperatories.filter(id => id !== op.id)); }} />
                            <label htmlFor={`new-op-${op.id}`} className="text-sm text-slate-700 cursor-pointer">{op.name}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <Button onClick={handleCreateUser} disabled={loading} className="w-full bg-dp-600 hover:bg-dp-700 text-white">{loading ? 'Creating...' : 'Add Team Member'}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Team Members List */}
        <div className="space-y-3">
          {users.map(u => (
            <div key={u.id} data-testid={`user-item-${u.username}`} className={`rounded-xl p-4 border flex items-center justify-between transition-colors ${darkMode ? 'bg-slate-700/50 border-slate-600 hover:bg-slate-700/70' : 'bg-white/50 border-white/20 hover:bg-white/70'}`}>
              <div className="flex items-center gap-3">
                {editMode && (
                  <button onClick={() => handleDeleteClick(u)} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-red-900/50 text-red-400' : 'hover:bg-red-100 text-red-500'}`} title="Delete team member">
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
                <div className="relative group">
                  {u.profile_picture ? (
                    <img src={u.profile_picture} alt={u.full_name} className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm" />
                  ) : (
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm border-2 shadow-sm ${darkMode ? 'bg-slate-600 border-slate-500 text-slate-300' : 'bg-slate-200 border-white text-slate-500'}`}>
                      {u.full_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                    <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleProfilePictureUpload(u.id, file); }} />
                    {u.profile_picture ? (
                      <button onClick={(e) => { e.stopPropagation(); handleRemoveProfilePicture(u.id); }} className="text-white text-xs"><X className="w-4 h-4" /></button>
                    ) : <span className="text-white text-xs">+</span>}
                  </div>
                </div>
                <div className={editMode ? "cursor-pointer hover:text-dp-600" : ""} onClick={() => editMode && handleEditUser(u)}>
                  <h3 className={`font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{u.full_name}</h3>
                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>@{u.username}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${darkMode ? 'bg-sky-900/50 text-sky-300' : 'bg-dp-100 text-dp-700'}`}>{getRoleLabel(u.base_role || u.role)}</div>
                {u.is_owner && <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}><Shield className="w-3.5 h-3.5" />Owner</div>}
                {(u.role === 'admin' || u.is_admin) && !u.is_owner && <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${darkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700'}`}><Shield className="w-3.5 h-3.5" />Admin</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="glass-panel max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Edit Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <div className="flex items-center gap-2 mt-2">
                  {(editRole === 'doctor' || editingUser?.base_role === 'doctor') && <span className="text-slate-500 font-medium">Dr.</span>}
                  <Input value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} className="flex-1" />
                </div>
              </div>
              <div><Label>Last Name</Label><Input value={editLastName} onChange={(e) => setEditLastName(e.target.value)} className="mt-2" /></div>
            </div>
            <div><Label>Username (Tag)</Label><Input value={editUsername} onChange={(e) => setEditUsername(e.target.value)} className="mt-2" /></div>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label>Role</Label>
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>{TEAM_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="text-center block">Admin Designation</Label>
                <div className="flex items-center justify-center mt-4">
                  <Checkbox id="edit-admin-checkbox" checked={editIsAdmin} onCheckedChange={setEditIsAdmin} disabled={editingUser?.is_owner && currentUser?.id !== editingUser?.id} className="h-5 w-5" />
                  <label htmlFor="edit-admin-checkbox" className="ml-2 text-sm text-slate-600">{editingUser?.is_owner ? 'Owner Admin' : 'Grant Admin'}</label>
                </div>
                {editingUser?.is_owner && currentUser?.id !== editingUser?.id && <p className="text-xs text-amber-600 mt-2 text-center">Owner status protected</p>}
              </div>
            </div>
            {(editRole === 'doctor' || editingUser?.base_role === 'doctor') && (
              <div className="p-3 rounded-lg bg-dp-50 border border-dp-200">
                <Label className="mb-2 block text-dp-800">Display Name Preference</Label>
                <p className="text-xs text-dp-600 mb-3">How should this doctor's name appear? (e.g., "Dr. {editDrDisplayPreference === 'first' ? editFirstName : editLastName}")</p>
                <div className="flex gap-4">
                  <div className="flex items-center"><Checkbox id="edit-dr-pref-first" checked={editDrDisplayPreference === 'first'} onCheckedChange={(c) => c && setEditDrDisplayPreference('first')} /><label htmlFor="edit-dr-pref-first" className="ml-2 text-sm text-slate-700 cursor-pointer">Dr. {editFirstName || 'FirstName'}</label></div>
                  <div className="flex items-center"><Checkbox id="edit-dr-pref-last" checked={editDrDisplayPreference === 'last'} onCheckedChange={(c) => c && setEditDrDisplayPreference('last')} /><label htmlFor="edit-dr-pref-last" className="ml-2 text-sm text-slate-700 cursor-pointer">Dr. {editLastName || 'LastName'}</label></div>
                </div>
              </div>
            )}
            {!['front_desk', 'front_office', 'office_manager', 'assistant'].includes(editRole) && (
              <div>
                <Label className="mb-2 block">Assigned Operatories</Label>
                <p className="text-xs text-slate-500 mb-3">Select operatories where this provider will work</p>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg bg-slate-50">
                  {operatories.map(op => (
                    <div key={op.id} className="flex items-center space-x-2">
                      <Checkbox id={`edit-op-${op.id}`} checked={editAssignedOperatories.includes(op.id)} onCheckedChange={(c) => { if (c) setEditAssignedOperatories([...editAssignedOperatories, op.id]); else setEditAssignedOperatories(editAssignedOperatories.filter(id => id !== op.id)); }} />
                      <label htmlFor={`edit-op-${op.id}`} className="text-sm text-slate-700 cursor-pointer">{op.name}</label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {smartwatchSeats && (
              <div className="p-4 rounded-lg bg-teal-50 border border-teal-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Watch className="w-5 h-5 text-teal-600" /><Label className="text-teal-800 font-medium">Smartwatch Access</Label></div>
                  {editHasSmartwatchAccess ? (
                    <Button variant="outline" size="sm" onClick={() => handleToggleSmartwatchAccess(false)} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="revoke-smartwatch-btn">Revoke Access</Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => handleToggleSmartwatchAccess(true)} disabled={smartwatchSeats.available <= 0} className="text-teal-600 border-teal-300 hover:bg-teal-100" data-testid="grant-smartwatch-btn">Grant Access</Button>
                  )}
                </div>
                <div className="mt-2">
                  {editHasSmartwatchAccess ? (
                    <p className="text-sm text-teal-700">This user has smartwatch access enabled.</p>
                  ) : (
                    <p className="text-sm text-slate-600">{smartwatchSeats.available > 0 ? `${smartwatchSeats.available} seat(s) available. Grant access to allow smartwatch use.` : 'No seats available. Purchase more seats from the License page.'}</p>
                  )}
                </div>
              </div>
            )}
            <Button onClick={handleSaveUserEdit} disabled={loading} className="w-full bg-dp-600 hover:bg-dp-700 text-white">{loading ? 'Saving...' : 'Save Changes'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team Member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.full_name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
